import { flashStore } from '@/store';
import { MOBILE_SCREEN_WIDTH } from '@/utils/constants';
import { isArray, max, min, trim } from 'lodash';
import i18n from '../i18n';
import { User } from '@/graphql/operations';
import { DateTime } from 'luxon';

const { t } = i18n.global;

const includeTag = (
    tagName: string,
    attributes: Record<string, string>
): void => {
    const scriptElement = document.createElement(tagName);

    Object.keys(attributes).forEach((attributeName) => {
        const attributeValue = attributes[attributeName];

        scriptElement.setAttribute(attributeName, attributeValue);
    });

    scriptElement.onload = function () {
        const DOMContentLoadedEvent = document.createEvent('Event');
        DOMContentLoadedEvent.initEvent('DOMContentLoaded', true, true);
        window.document.dispatchEvent(DOMContentLoadedEvent);
    };

    document.getElementsByTagName('head')[0].appendChild(scriptElement);
};

const includeScript = (url: string): void => {
    const scriptElement = document.createElement('script');
    scriptElement.setAttribute('src', url);
    document.head.appendChild(scriptElement);
};

const formatPhone = (phone: string | null) => {
    if (!phone) {
        return '';
    }

    // Remove all non-digit characters
    const cleanPhone = phone.replace(/[^\d]/g, '');

    // Define formatting patterns based on the length
    const formattingPatterns: Record<number, string> = {
        7: 'xxx-xxxx',
        10: 'xxx-xxx-xxxx',
        11: 'x-xxx-xxx-xxxx',
        12: '+xx xxx xxx xxxx'
    };

    // Check if the length matches a formatting pattern
    if (formattingPatterns[cleanPhone.length]) {
        const pattern = formattingPatterns[cleanPhone.length];
        let formattedPhone = '';
        let index = 0;

        // Replace pattern placeholders with digits from the cleaned phone number
        for (let i = 0; i < pattern.length; i++) {
            if (pattern[i] === 'x') {
                formattedPhone += cleanPhone[index];
                index += 1;
            } else {
                formattedPhone += pattern[i];
            }
        }

        return formattedPhone;
    }

    // If the length doesn't match any pattern, return the cleaned phone number as is
    return cleanPhone;
};

const formatStrings = (stringArray: string[]) => {
    return trim(stringArray.join(' '));
};

const generateUuid = (): string => {
    let result;
    let i;
    let j;
    result = '';
    for (j = 0; j < 32; j++) {
        if (j === 8 || j === 12 || j === 16 || j === 20) result += '-';
        i = Math.floor(Math.random() * 16)
            .toString(16)
            .toUpperCase();
        result += i;
    }

    return result;
};

const randomIntFromInterval = (minimum: number, maximum: number): number => {
    return Math.floor(Math.random() * (maximum - minimum + 1) + minimum);
};

const setLimits = (value: number, upperLimit = 100, lowerLimit = 0) => {
    return max([min([value, upperLimit]), lowerLimit]) as number;
};

const delay = (ms: number) => {
    return new Promise((resolve) => {
        return setTimeout(resolve, ms);
    });
};

const stringToInt = (s: string | number): number => {
    if (typeof s === 'string') {
        return Number(s.replace(/\D/g, ''));
    } else {
        return s;
    }
};

const validateAddComma = (s: string): string => {
    return s.replace(/\D/g, '').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

const getRandomItem = (items: string[]) => {
    return items[Math.floor(Math.random() * items.length)];
};

const isMobileView = (): boolean => {
    return window.innerWidth < MOBILE_SCREEN_WIDTH;
};

const removeUrlAppend = (url: string, separator = '?') => {
    const parts = url.split(separator);

    if (isArray(parts)) {
        return parts[0];
    }

    return url;
};

const getBaseUrl = (): string => {
    return import.meta.env.VITE_BASE_URL ?? '';
};

const openUrlInNewTab = (url: string): void => {
    window?.open(url, '_blank')?.focus();
};

const truncateString = (value: string, limit: number) => {
    if (value.length > limit) {
        return value.slice(0, limit).trimEnd() + '...';
    }
    return value;
};

const getExtensionFromString = (s: string): string => {
    const parts = s.match(/(?:\.([^.]+))?$/);
    return parts?.length ? parts[0] : '';
};

const pickProperties = (obj: any, keys: any) => {
    return Object.keys(obj).filter(k => {
        return keys.includes(k);
    }).reduce((res, k) => {
        return Object.assign(res, { [k]: obj[k] });
    }, {});
};

const getUrlParam = (param: string): string | null => {
    return new URLSearchParams(window.location.search).get(param);
};

const setFocusOnFirstInput = () => {
    const elements = document.getElementsByTagName('input');
    if (elements.length) {
        elements[0].focus();
    }
};

const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const copyToClipboard = (text: string, message = t('flash.copied')) => {
    navigator.clipboard.writeText(text).then(() => {
        return flashStore.doFlash({ message: message });
    });
};

const setCookie = (name: string, value: string, days = 7) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);

    let cookieValue = encodeURIComponent(value) + '; expires=' + expirationDate.toUTCString() + '; path=/';

    const hostname = window.location.hostname;

    if (hostname) {
        // Regular expression to extract the domain from the hostname
        const domainMatch = hostname.match(/(?:\.([^.]+)){1,2}\.([^.]+)$/);

        // If a match is found, set the extracted domain as the cookie domain; otherwise, use the full hostname as the domain
        const domain = domainMatch ? domainMatch[0] : hostname;

        cookieValue += '; domain=' + domain;
    }

    document.cookie = name + '=' + cookieValue;
};

const scrollToElement = (elementId: string, position: ScrollLogicalPosition = 'start') => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: position });
};

const isLocalStorageAvailable = (): boolean => {
    try {
        if (typeof window.localStorage === 'undefined') return false;

        const test = 'test';
        localStorage.setItem(test, test);
        if (localStorage.getItem(test) !== test) return false;
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
};

const downloadData = (data: string, fileName: string): void => {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = data;
    link.click();
};

const getPageSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    return parts[0];
};

const getUserName = (user: User): string => {
    return user.person?.firstName ? `${user.person.firstName} ${user.person.lastName}` : user.email ?? '';
};

const getMaxStartAndEndDates = (startDate?: string | null, endDate?: string | null, maxStartDateAsCurrent = true) => {
    const defaultMaxStartDate = maxStartDateAsCurrent ? DateTime.now().toISODate() : undefined;
    const maxStartDate = (endDate ? DateTime.fromISO(endDate).toISODate() : defaultMaxStartDate) ?? undefined;
    const minEndDate = startDate ? (DateTime.fromISO(startDate).toISODate() ?? undefined) : undefined;
    const maxEndDate = DateTime.now().toISODate();

    return { maxStartDate, minEndDate, maxEndDate };
};

const extractNextOccurrence = (type: string, rawDate: string): Date => {
    const date = new Date(rawDate);

    switch (type) {
        case 'weekly': {
            const nextWeek = new Date(date.setDate(date.getDate() + 7));
            return nextWeek;
        }
        case 'every_other_week': {
            const nextOtherWeek = new Date(date.setDate(date.getDate() + 14));
            return nextOtherWeek;
        }
        case 'twice_a_month': {
            const dayOfTheMonth = date.getDate();

            if (dayOfTheMonth > 14) {
                const nextMonth = new Date(date.setMonth(date.getMonth() + 1));
                return new Date(nextMonth.setDate(nextMonth.getDate() - 14));
            }

            return new Date(date.setDate(date.getDate() + 14));
        }
        case 'monthly':
        default: {
            const isLastDayOfMonth = date.getDate() > 28;

            // Get the last day of the next month instead of just adding a month.
            if (isLastDayOfMonth) {
                const nextMonth = new Date(date.setMonth(date.getMonth() + 1));
                const lastDayOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);
                return lastDayOfNextMonth;
            }

            return new Date(date.setMonth(date.getMonth() + 1));
        }
    }
};

export {
    includeScript,
    includeTag,
    formatPhone,
    generateUuid,
    formatStrings,
    randomIntFromInterval,
    delay,
    stringToInt,
    validateAddComma,
    getRandomItem,
    isMobileView,
    removeUrlAppend,
    getBaseUrl,
    openUrlInNewTab,
    truncateString,
    getExtensionFromString,
    pickProperties,
    getUrlParam,
    setFocusOnFirstInput,
    setLimits,
    capitalizeFirstLetter,
    copyToClipboard,
    setCookie,
    scrollToElement,
    isLocalStorageAvailable,
    downloadData,
    getPageSubdomain,
    getUserName,
    getMaxStartAndEndDates,
    extractNextOccurrence
};
